import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse JWT token to get expiration time
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Check token expiration
  const checkTokenExpiration = useCallback(() => {
    if (!token) return;
    
    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      logout();
      return;
    }
    
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now) {
      setSessionExpired(true);
      logout();
    }
  }, [token, logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSessionExpired(false);
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response?.status === 401) {
        setSessionExpired(true);
      }
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      // Check token expiration on mount
      const payload = parseJwt(token);
      if (payload && payload.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp < now) {
          setSessionExpired(true);
          logout();
          setLoading(false);
          return;
        }
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      
      // Set up periodic token expiration check (every minute)
      const interval = setInterval(checkTokenExpiration, 60000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [token, logout, checkTokenExpiration, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    return userData;
  };

  const register = async (name, email, password, role = 'sales') => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    return userData;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    sessionExpired,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isAccountant: user?.role === 'accountant' || user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
